import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Phone, Upload, ChevronDown, ChevronRight, PlayCircle, PauseCircle,
  Lock, Trash2,
} from 'lucide-react';
import { usePageHeader } from '../context/PageHeaderContext';
import { toast } from 'sonner';
import * as perms from '../lib/permissions';

const TYPES = [
  { value: 'audio_force',                label: 'Pre-recorded msg (force listen)' },
  { value: 'audio_no_force',             label: 'Pre-recorded msg (no force listen)' },
  { value: 'audio_force_first_only',     label: 'Pre-recorded msg (force listen first time only)' },
  { value: 'route_state',                label: 'Route to statewide number' },
  { value: 'route_member',               label: 'Route to a specific member' },
  { value: 'route_ai_agent',             label: 'Route to AI agent' },
  { value: 'audio_force_then_route_member', label: 'Pre-recorded msg → route to assigned member' },
];

const AudioField = ({ value, filename, onUpload, disabled = false }) => {
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const fullUrl = value ? `${backendUrl}${value}` : null;
  return (
    <div className="flex items-center gap-1.5">
      {fullUrl && (
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={() => {
            if (!audioRef.current) {
              audioRef.current = new Audio(fullUrl);
              audioRef.current.onended = () => { setPlaying(false); audioRef.current = null; };
            }
            if (playing) { audioRef.current.pause(); audioRef.current = null; setPlaying(false); }
            else { audioRef.current.play().catch(() => {}); setPlaying(true); }
          }}
          data-testid="audio-preview"
        >
          {playing ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
        </Button>
      )}
      {filename && <span className="text-xs text-muted-foreground truncate max-w-[160px]">{filename}</span>}
      <Button size="sm" variant="outline" className="h-7" disabled={disabled} onClick={() => inputRef.current?.click()}>
        <Upload className="w-3.5 h-3.5 mr-1" />
        {value ? 'Replace' : 'Upload'}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/m4a,audio/wav,audio/ogg,audio/aac,.mp3,.m4a,.wav,.ogg,.aac"
        hidden
        onChange={(e) => { if (e.target.files?.[0]) onUpload(e.target.files[0]); e.target.value = ''; }}
      />
    </div>
  );
};

/**
 * ScopeBlock — collapsible IVR editor for ONE phone_routing_configs doc.
 *
 *   scopeKey: state code (`NC`) | `_default` | `_exempt_priv_<uid>` | `_exempt_group_<gid>`
 *   title: rendered label
 *   subtitle: rendered under the label (phone number, etc.)
 *   canEdit: gates every mutation in the block
 *   defaultOpen: if true, opens by default
 *   rightAccessory: optional ReactNode (used to render the "Apply to all
 *                   standard states" controls inside the _default block)
 */
const ScopeBlock = ({
  scopeKey, title, subtitle, canEdit = true, defaultOpen = false,
  eligibleUsers = [], rightAccessory = null, testid,
}) => {
  const { api } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const [pending, setPending] = useState({});
  const [savingDigit, setSavingDigit] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/phone-routing/configs/${encodeURIComponent(scopeKey)}`);
      setCfg(res.data);
      setPending({});
    } catch (e) { /* ignore */ }
  }, [api, scopeKey]);
  useEffect(() => { if (open && !cfg) load(); }, [open, cfg, load]);

  const liveDigit = (d) => ({ ...((cfg?.digits || {})[d] || {}), ...(pending[d] || {}) });
  const isDirty = (d) => Object.keys(pending[d] || {}).length > 0;

  const stagePatch = (d, patch) => setPending((p) => ({ ...p, [d]: { ...(p[d] || {}), ...patch } }));

  const saveDigit = async (d) => {
    if (!isDirty(d)) return;
    setSavingDigit(d);
    try {
      await api.put(`/phone-routing/configs/${encodeURIComponent(scopeKey)}/digits/${d}`, pending[d]);
      toast.success(`Key ${d} saved`);
      setPending((p) => { const n = { ...p }; delete n[d]; return n; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Save failed for key ${d}`);
    } finally { setSavingDigit(null); }
  };

  const toggleActive = async (d, v) => {
    try {
      await api.put(`/phone-routing/configs/${encodeURIComponent(scopeKey)}/digits/${d}`, { active: v });
      toast.success(`Key ${d} ${v ? 'enabled' : 'disabled'}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  const uploadAudio = async (digit, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(
        `/phone-routing/configs/${encodeURIComponent(scopeKey)}/digits/${digit}/upload`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Audio saved');
      await load();
    } catch (e) { toast.error('Upload failed'); }
  };

  const removeAudio = async (digit) => {
    try {
      await api.put(`/phone-routing/configs/${encodeURIComponent(scopeKey)}/digits/${digit}`, { clear_audio: true });
      toast.success('Audio removed');
      await load();
    } catch (e) { toast.error('Could not remove audio'); }
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-[#1a1a1a]/70" data-testid={testid || `scope-${scopeKey}`}>
      <div className="flex items-center justify-between gap-2 p-3">
        <button onClick={() => setOpen(!open)} className="flex-1 flex items-center gap-2 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          {scopeKey === '_default' ? (
            <Phone className="w-4 h-4 text-amber-600" />
          ) : (
            <Phone className="w-4 h-4 text-[#586D52]" />
          )}
          <span className="font-medium text-sm">{title}</span>
          {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
          {!canEdit && <Lock className="w-3.5 h-3.5 text-muted-foreground ml-1" />}
        </button>
        {rightAccessory}
      </div>
      {open && cfg && (
        <div className="px-3 pb-3 space-y-2">
          {!canEdit && (
            <div className="rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-700 p-2 text-[11px] text-amber-700 dark:text-amber-300">
              Read-only.
            </div>
          )}
          <fieldset disabled={!canEdit} className={!canEdit ? 'space-y-2 opacity-70 pointer-events-none' : 'space-y-2'}>
            <div className="rounded-lg p-2 bg-gray-50 dark:bg-[#0d0d0d] space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-[#586D52] w-16 shrink-0">Welcome</span>
                <select
                  value={(pending.welcome?.type ?? cfg.welcome?.type) || 'audio_force_first_only'}
                  onChange={(e) => setPending((p) => ({ ...p, welcome: { ...(p.welcome || {}), type: e.target.value } }))}
                  className="flex-1 min-w-0 h-8 text-xs rounded border border-input bg-background px-2 truncate"
                  data-testid={`welcome-type-${scopeKey}`}
                >
                  <option value="audio_force">Force listen (no skip)</option>
                  <option value="audio_no_force">No force listen (caller can press digit anytime)</option>
                  <option value="audio_force_first_only">Force listen first time only</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-16 shrink-0">Audio</span>
                <AudioField value={cfg.welcome?.audio_url} filename={cfg.welcome?.filename} onUpload={(f) => uploadAudio('welcome', f)} disabled={!canEdit} />
              </div>
              {pending.welcome?.type && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="h-7 bg-[#586D52] hover:bg-[#4a5d46]"
                    disabled={savingDigit === 'welcome'}
                    onClick={async () => {
                      setSavingDigit('welcome');
                      try {
                        await api.put(`/phone-routing/configs/${encodeURIComponent(scopeKey)}/welcome`, pending.welcome);
                        toast.success('Welcome saved');
                        setPending((p) => { const n = { ...p }; delete n.welcome; return n; });
                        await load();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
                      finally { setSavingDigit(null); }
                    }}
                    data-testid={`welcome-save-${scopeKey}`}
                  >
                    {savingDigit === 'welcome' ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7"
                    onClick={() => setPending((p) => { const n = { ...p }; delete n.welcome; return n; })}
                  >Cancel</Button>
                </div>
              )}
            </div>

            {Array.from({ length: 10 }, (_, i) => String(i)).map((d) => {
              const k = liveDigit(d);
              const dirty = isDirty(d);
              return (
                <div key={d} className={`rounded-lg border p-2 ${k.active ? 'border-[#586D52]/40 bg-[#586D52]/5' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0d0d0d]'}`} data-testid={`digit-row-${scopeKey}-${d}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-lg w-7 text-center shrink-0">{d}</span>
                    <Switch checked={!!k.active} onCheckedChange={(v) => toggleActive(d, v)} data-testid={`digit-toggle-${scopeKey}-${d}`} />
                    <select
                      value={k.type || ''}
                      onChange={(e) => stagePatch(d, { type: e.target.value })}
                      className="flex-1 min-w-0 h-8 text-xs rounded border border-input bg-background px-2 truncate"
                      data-testid={`digit-type-${scopeKey}-${d}`}
                    >
                      <option value="">— select type —</option>
                      {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  {k.type && (k.type.startsWith('audio_') || k.type === 'audio_force_then_route_member') && (
                    <div className="mt-1.5 ml-9 flex items-center gap-2">
                      <AudioField value={k.audio_url} filename={k.filename} onUpload={(f) => uploadAudio(d, f)} disabled={!canEdit} />
                      {k.audio_url && canEdit && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeAudio(d)} title="Remove audio" data-testid={`digit-remove-audio-${scopeKey}-${d}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-600" />
                        </Button>
                      )}
                    </div>
                  )}
                  {k.type === 'route_member' && (
                    <div className="mt-1.5 ml-9">
                      <select
                        value={k.target_user_id || ''}
                        onChange={(e) => stagePatch(d, { target_user_id: e.target.value })}
                        className="h-8 text-xs rounded border border-input bg-background px-2 max-w-full"
                      >
                        <option value="">— select member —</option>
                        {eligibleUsers.map((u) => (
                          <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.role})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {dirty && (
                    <div className="mt-2 ml-9 flex items-center gap-2">
                      <Button size="sm" className="h-7 bg-[#586D52] hover:bg-[#4a5d46]" disabled={savingDigit === d} onClick={() => saveDigit(d)} data-testid={`digit-save-${scopeKey}-${d}`}>
                        {savingDigit === d ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => setPending((p) => { const n = { ...p }; delete n[d]; return n; })}>Cancel</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </fieldset>
        </div>
      )}
    </div>
  );
};

/** "Apply to all standard states" accessory — lives ONLY on _default block. */
const DefaultApplyButton = ({ api, canEdit }) => {
  const [open, setOpen] = useState(false);
  const [includeExemptions, setIncludeExemptions] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.post('/phone-routing/default/apply', { include_exemptions: includeExemptions });
      const d = res.data || {};
      toast.success(`Applied to ${d.applied_standard_states?.length || 0} standard state(s)${includeExemptions ? ` + ${d.applied_exemptions?.length || 0} exemption(s)` : ''}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" disabled={!canEdit} className="h-8 bg-amber-600 hover:bg-amber-700 text-white" onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid="default-apply-btn">
        Apply
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="default-apply-dialog">
          <DialogHeader>
            <DialogTitle>Apply Standard State-Wide Default</DialogTitle>
            <DialogDescription>
              Overwrites every standard state&apos;s routing config. Custom per-state adjustments will be replaced.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={includeExemptions} onCheckedChange={setIncludeExemptions} data-testid="default-apply-include-exemptions" />
            Also apply to all Exemption states
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={run} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="default-apply-confirm">
              {busy ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Inline exemption metadata editor lives on /admin/statewide-details
 *  (StatewideDetailsPage). The Phone Routing page is for routing only. */

const PhoneRoutingPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const phonePerm = useMemo(() => perms.phoneRouting(user), [user]);
  const canAccess = phonePerm.view;

  usePageHeader({ title: 'Phone Routing', back: '/admin/calling' }, []);

  const [scopes, setScopes] = useState({ default: null, standard: [], restricted: [], exemptions: [] });
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const initialTab = (phonePerm.standard || phonePerm.exemption) ? 'standard' : 'restricted';
  const [tab, setTab] = useState(initialTab);

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/phone-routing/scopes');
      setScopes(r.data || { default: null, standard: [], restricted: [], exemptions: [] });
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => {
    if (!canAccess) return;
    reload();
    api.get('/users/all').then((r) => setEligibleUsers(r.data || [])).catch(() => {});
  }, [canAccess, reload, api]);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background p-8 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-medium">Admin only</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Back</Button>
      </div>
    );
  }

  const tabs = [
    { id: 'standard', label: 'Standard States', show: phonePerm.standard || phonePerm.exemption },
    { id: 'restricted', label: 'Restricted States', show: phonePerm.restricted },
  ].filter((t) => t.show);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="phone-routing-page">
      <div className="max-w-3xl mx-auto p-3">
        <Card className="rounded-2xl mb-3">
          <CardContent className="pt-3 pb-3">
            <div className="flex gap-2 flex-wrap" data-testid="phone-routing-tabs">
              {tabs.map((t) => (
                <Button
                  key={t.id}
                  size="sm"
                  variant={tab === t.id ? 'default' : 'outline'}
                  onClick={() => setTab(t.id)}
                  className={tab === t.id ? (t.id === 'restricted' ? 'bg-red-500 hover:bg-red-600' : 'bg-[#586D52] hover:bg-[#4a5d46]') : ''}
                  data-testid={`phone-routing-tab-${t.id}`}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {tab === 'standard' && (
          <div className="space-y-3">
            {phonePerm.standard && (
              <Card className="rounded-2xl">
                <CardContent className="pt-3 space-y-2">
                  <ScopeBlock
                    scopeKey="_default"
                    title="Standard State-Wide Default"
                    subtitle="Edits propagate to all Standard states on Apply"
                    canEdit={phonePerm.standard}
                    defaultOpen={false}
                    eligibleUsers={eligibleUsers}
                    rightAccessory={<DefaultApplyButton api={api} canEdit={phonePerm.standard} />}
                    testid="scope-_default"
                  />
                  <div className="text-[11px] text-muted-foreground px-1 pt-2">Standard states (each has its own config):</div>
                  {scopes.standard.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No standard state numbers configured.</p>
                  ) : scopes.standard.map((s) => (
                    <ScopeBlock
                      key={s.state}
                      scopeKey={s.state}
                      title={s.state}
                      subtitle={s.phone_number}
                      canEdit={phonePerm.standard}
                      eligibleUsers={eligibleUsers}
                    />
                  ))}
                </CardContent>
              </Card>
            )}

            {phonePerm.exemption && (
              <Card className="rounded-2xl">
                <CardContent className="pt-3 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground">Exemptions</p>
                  {scopes.exemptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No exemptions configured. Add them in Statewide Details.</p>
                  ) : scopes.exemptions.map((ex) => (
                    <ScopeBlock
                      key={ex.scope_key}
                      scopeKey={ex.scope_key}
                      title={ex.name}
                      subtitle={ex.phone_number}
                      canEdit={phonePerm.exemption}
                      eligibleUsers={eligibleUsers}
                    />
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {tab === 'restricted' && phonePerm.restricted && (
          <Card className="rounded-2xl">
            <CardContent className="pt-3 space-y-2">
              {scopes.restricted.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No restricted state numbers configured.</p>
              ) : scopes.restricted.map((s) => (
                <ScopeBlock
                  key={s.state}
                  scopeKey={s.state}
                  title={s.state}
                  subtitle={s.phone_number}
                  canEdit={phonePerm.restricted}
                  eligibleUsers={eligibleUsers}
                />
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PhoneRoutingPage;
